import { cn } from '@repo/tailwind-config/tailwind.functions'
import {
  ChangeEvent,
  createElement,
  forwardRef,
  InputHTMLAttributes,
  useEffect,
  useImperativeHandle,
  useRef
} from 'react'

import { typography } from '../typography'
import { PlaceholderVariant } from '.'

type TypedCaret = {
  value: string
  selectionStart: number
  selectionEnd: number
}

/**
 * Position in `value` that leaves `charsAfter` non-whitespace characters after the caret,
 * so the caret stays next to the character just typed when a formatter inserts or removes spaces.
 */
const getCaretPosition = (value: string, charsAfter: number) => {
  let position = value.length
  let count = 0

  while (position > 0 && count < charsAfter) {
    position--
    if (!/\s/.test(value.charAt(position))) count++
  }

  return position
}

const countNonWhitespace = (value: string) => value.replace(/\s/g, '').length

/**
 * When the value is rewritten while typing (first letter capitalized, IBAN formatted...),
 * React replaces the input value and the browser moves the caret to the end: put it back.
 */
const restoreTypedCaret = (
  input: HTMLInputElement | HTMLTextAreaElement,
  typedCaret: TypedCaret
) => {
  if (input.value === typedCaret.value || input.ownerDocument.activeElement !== input) return

  if (input.value.length === typedCaret.value.length) {
    input.setSelectionRange(typedCaret.selectionStart, typedCaret.selectionEnd)
    return
  }

  const position = getCaretPosition(
    input.value,
    countNonWhitespace(typedCaret.value.slice(typedCaret.selectionEnd))
  )
  input.setSelectionRange(position, position)
}

export interface BaseInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>,
  'children' | 'value'
> {
  value?: string | number | null
  rows?: number
  highlighted?: boolean
  inputClassName?: string
  placeholderVariant?: PlaceholderVariant
  setIsActive: (value: boolean) => void
}

const BaseInput = forwardRef<HTMLInputElement, BaseInputProps>(
  ({ setIsActive, highlighted, inputClassName, placeholderVariant = 'light', ...props }, ref) => {
    const internalRef = useRef<HTMLInputElement>(null)

    useImperativeHandle(ref, () => internalRef.current as HTMLInputElement)

    useEffect(() => {
      if (internalRef.current) {
        setIsActive(internalRef.current.value.length > 0)
      }
    }, [setIsActive, props.value])

    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const input = e.target
      const { selectionStart, selectionEnd } = input
      const typedValue = input.value
      let value = typedValue

      if (props.type === 'text' && props.autoCapitalize !== 'off') {
        // Capitalize the first letter and keep the rest as it is
        value = value.charAt(0).toUpperCase() + value.slice(1)
      }

      // Update the value in the state or form
      // Call the original onChange handler with the updated value
      props.onChange?.({ ...e, target: { ...e.target, value: value } })

      setIsActive(value.length > 0)

      // Runs once React has re-rendered and restored the controlled value
      if (selectionStart !== null && selectionEnd !== null) {
        queueMicrotask(() =>
          restoreTypedCaret(input, { value: typedValue, selectionStart, selectionEnd })
        )
      }
    }

    const baseProps = {
      ...props,
      ref: internalRef,
      className: cn(
        'focus:outline-none border-none bg-transparent w-full h-full duration-2 caret-primary text-black placeholder:select-none disabled:cursor-not-allowed disabled:text-grey-light-active disabled:placeholder-gray-500',
        { 'focus-placeholder placeholder:opacity-0': props['aria-label'] },
        { 'bg-primary-light': highlighted },
        { 'placeholder:text-grey placeholder:font-normal': placeholderVariant === 'dark' },
        props.className,
        inputClassName
      ),
      style: {
        ...typography['label-medium'],
        ...props.style
      },
      onChange: handleChange,
      'aria-required': props['aria-required'] ?? props.required
    }

    return props.type === 'textarea'
      ? createElement('textarea', {
          ...baseProps,
          rows: props.rows ?? 4,
          value: props.value ?? '',
          style: {
            ...baseProps.style,
            background: 'transparent',
            resize: 'none'
          }
        })
      : createElement('input', {
          ...baseProps,
          value: props.value ?? '',
          type: props.type ?? 'text'
        })
  }
)

BaseInput.displayName = 'BaseInput'

export default BaseInput
